/**
 * Analytics Service
 * Handles learning analytics, progress tracking, and reports
 */

import { apiClient, extractData, withRetry } from './api';
import type { ChildActivitySummary } from '@kids-lms/types';

export interface ConversationLog {
  id: string;
  childId: string;
  subject: string | null;
  flagged: boolean;
  platform: string;
  messageCount: number;
  createdAt: string;
  lastMessageAt: string;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
  }>;
  flagCount: number;
}

export interface ConversationLogsResponse {
  conversations: ConversationLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Helper to transform snake_case to camelCase for conversations
function transformConversation(conv: Record<string, unknown>): ConversationLog {
  const rawMessages = Array.isArray(conv.messages) ? conv.messages : [];
  return {
    id: (conv.id as string) || '',
    childId: (conv.child_id || conv.childId || '') as string,
    subject: (conv.subject as string) || null,
    flagged: Boolean(conv.flagged || (conv.flagCount && (conv.flagCount as number) > 0) || (conv.flag_count && (conv.flag_count as number) > 0)),
    platform: (conv.platform as string) || 'web',
    messageCount: (conv.message_count || conv.messageCount || 0) as number,
    createdAt: (conv.created_at || conv.createdAt || new Date().toISOString()) as string,
    lastMessageAt: (conv.last_message_at || conv.lastMessageAt || conv.created_at || conv.createdAt || new Date().toISOString()) as string,
    messages: rawMessages.map((msg: Record<string, unknown>) => ({
      id: (msg.id as string) || '',
      role: (msg.role as 'user' | 'assistant') || 'user',
      content: (msg.content as string) || '',
      createdAt: (msg.created_at || msg.createdAt || new Date().toISOString()) as string,
    })),
    flagCount: (conv.flagCount || conv.flag_count || 0) as number,
  };
}

export interface ConversationSummary {
  conversationId: string;
  summary: string;
}

export interface GetLogsParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  subject?: string;
  flagged?: boolean;
}

/**
 * Get child's conversation history with full details
 */
export async function getChildLogs(
  childId: string,
  params: GetLogsParams = {}
): Promise<ConversationLogsResponse> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: { conversations: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }>(
      `/logs/child/${childId}`,
      { params }
    )
  );
  const data = extractData(response);

  // Transform snake_case to camelCase
  return {
    conversations: (data.conversations || []).map(transformConversation),
    pagination: data.pagination,
  };
}

/**
 * Get child's activity summary
 */
export async function getChildSummary(
  childId: string,
  startDate?: string,
  endDate?: string
): Promise<ChildActivitySummary> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ChildActivitySummary }>(
      `/logs/child/${childId}/summary`,
      { params: { startDate, endDate } }
    )
  );
  return extractData(response);
}

/**
 * Get specific conversation with full details
 */
export async function getConversationDetails(conversationId: string): Promise<{
  conversation: ConversationLog & {
    flags: unknown[];
    moderationLogs: unknown[];
  };
}> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: {
      conversation: ConversationLog & {
        flags: unknown[];
        moderationLogs: unknown[];
      };
    } }>(`/logs/conversation/${conversationId}`)
  );
  return extractData(response);
}

/**
 * Generate AI summary of a conversation
 */
export async function summarizeConversation(conversationId: string): Promise<ConversationSummary> {
  const response = await apiClient.post<{ success: boolean; data: ConversationSummary }>(
    `/logs/conversation/${conversationId}/summarize`
  );
  return extractData(response);
}

/**
 * Get weekly report data for a child
 */
export async function getWeeklyReport(childId: string, weekStart?: string): Promise<ChildActivitySummary> {
  const start = weekStart || getWeekStart();
  const end = getWeekEnd(start);

  return getChildSummary(childId, start, end);
}

/**
 * Get daily activity for a child
 */
export async function getDailyActivity(
  childId: string,
  date?: string
): Promise<{
  date: string;
  conversations: number;
  messages: number;
  minutesSpent: number;
  subjectsStudied: string[];
}> {
  const targetDate = date || new Date().toISOString().split('T')[0];
  const summary = await getChildSummary(childId, targetDate, targetDate);

  const dayActivity = summary.dailyActivity.find((d) => d.date === targetDate);

  return {
    date: targetDate,
    conversations: dayActivity?.conversations || 0,
    messages: dayActivity?.messages || 0,
    minutesSpent: Math.round(summary.conversations.avgSessionMinutes),
    subjectsStudied: summary.subjects.map((s) => s.subject),
  };
}

/**
 * Get progress by subject for a child
 */
export async function getSubjectProgress(
  childId: string,
  startDate?: string,
  endDate?: string
): Promise<Array<{ subject: string; count: number; percentage: number }>> {
  const summary = await getChildSummary(childId, startDate, endDate);
  const total = summary.subjects.reduce((sum, s) => sum + s.count, 0);

  return summary.subjects.map((s) => ({
    subject: s.subject,
    count: s.count,
    percentage: total > 0 ? Math.round((s.count / total) * 100) : 0,
  }));
}

// Helper functions
function getWeekStart(date?: string): string {
  const d = date ? new Date(date) : new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

function getWeekEnd(weekStart: string): string {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  return d.toISOString().split('T')[0];
}

/**
 * Get flagged content for a specific child from the moderation endpoint
 */
export interface FlaggedContentRecord {
  id: string;
  conversation_id: string;
  message_id: string | null;
  child_id: string;
  child_name: string;
  flagged_by: 'system' | 'parent' | 'admin';
  severity: 'low' | 'medium' | 'high' | 'critical';
  reason: string;
  matched_patterns: string[];
  reviewed: boolean;
  reviewed_at: string | null;
  action_taken: string | null;
  review_notes: string | null;
  parent_notified: boolean;
  created_at: string;
}

export interface FlaggedContentResponse {
  items: FlaggedContentRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getFlaggedContent(
  childId: string,
  page: number = 1,
  limit: number = 50
): Promise<FlaggedContentResponse> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: FlaggedContentResponse }>(
      `/moderation/flagged/child/${childId}`,
      { params: { page, limit } }
    )
  );
  return extractData(response);
}

export default {
  getChildLogs,
  getChildSummary,
  getConversationDetails,
  summarizeConversation,
  getWeeklyReport,
  getDailyActivity,
  getSubjectProgress,
  getFlaggedContent,
};
